/** Sprint 5 · Form Builder · 檢視自己填寫的內容（read-only）+ 列印。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import EditNoteIcon from '@mui/icons-material/EditNote';

import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import { FieldReadonlyValue } from './components/FieldRenderer';
import { getWorkspaceForm, openPrint, FormDetail } from './services/formBuilderService';

const FUNC = 'form_workspace';

const FormViewPage: React.FC = () => {
  const { id } = useParams();
  const fid = Number(id);
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`formBuilder.${k}`, d);
  const { hasPermission, loading: permLoading } = usePermission();
  const { showApiError } = useMessage();
  const canPrint = hasPermission(FUNC, 'print');

  const [form, setForm] = useState<FormDetail | null>(null);
  const [data, setData] = useState<Record<string, unknown>>({});
  const [hasSub, setHasSub] = useState(false);

  const load = useCallback(async () => {
    try {
      const f = await getWorkspaceForm(fid); setForm(f);
      if (f.my_submission) { setData(f.my_submission.data || {}); setHasSub(true); }
    } catch (err) { showApiError(err); }
  }, [fid, showApiError]);

  useEffect(() => { if (!permLoading && hasPermission(FUNC, 'read')) load(); }, [permLoading, hasPermission, load]);

  if (permLoading) return null;
  if (!hasPermission(FUNC, 'read')) return <Box sx={{ p: 3 }}>{t('common.noPermission', '您沒有權限')}</Box>;

  const visible = (form?.fields || []).filter((f) => f.field_type !== 'hidden');

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      <Button size="small" onClick={() => nav('/form_workspace')}>← {tt('back', '返回工作區')}</Button>
      <Typography variant="h6" sx={{ my: 1 }}>{form?.title}</Typography>
      {!hasSub ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">{tt('notFilled', '你尚未填寫此表單。')}</Typography>
          <Button sx={{ mt: 1 }} variant="contained" startIcon={<EditNoteIcon />}
            onClick={() => nav(`/form_workspace/${fid}/fill`)}>{tt('fill', '前往填寫')}</Button>
        </Paper>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {visible.map((f) => (
              <FieldReadonlyValue key={f.field_name} field={f} value={data[f.field_name]} />
            ))}
          </Paper>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="outlined" startIcon={<EditNoteIcon />}
              onClick={() => nav(`/form_workspace/${fid}/fill`)}>{tt('edit', '編輯')}</Button>
            {canPrint && <Button variant="contained" startIcon={<PrintIcon />}
              onClick={() => openPrint(fid).catch(showApiError)}>{tt('print', '列印')}</Button>}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default FormViewPage;
